import { useCallback, useEffect, useMemo, useState } from 'react'

import { useStytchB2BClient } from '@stytch/react/b2b'

export type ConsentStatus =
  | 'loading'
  | 'consent'
  | 'submitting'
  | 'success'
  | 'error'

const SUCCESS_REDIRECT_DELAY_MS = 1500

type OAuthParams = {
  client_id: string
  redirect_uri: string
  response_type: string
  scopes: string[]
  state?: string
  nonce?: string
  code_challenge?: string
  prompt?: string
}

const parseOAuthParams = (): OAuthParams | null => {
  const params = new URLSearchParams(window.location.search)

  const client_id = params.get('client_id')
  const redirect_uri = params.get('redirect_uri')
  const response_type = params.get('response_type')

  if (!client_id || !redirect_uri || !response_type) {
    return null
  }

  return {
    client_id,
    redirect_uri,
    response_type,
    scopes: params.get('scope')?.split(' ').filter(Boolean) ?? [],
    state: params.get('state') ?? undefined,
    nonce: params.get('nonce') ?? undefined,
    code_challenge: params.get('code_challenge') ?? undefined,
    prompt: params.get('prompt') ?? undefined
  }
}

export const useOAuthConsent = () => {
  const stytch = useStytchB2BClient()

  // Read the OAuth params once on mount — they live on the URL and don't change.
  const oauthParams = useMemo(() => parseOAuthParams(), [])

  const [status, setStatus] = useState<ConsentStatus>('loading')

  // Callback URL from submit (carries the auth code); held in state for the success screen's manual "continue" fallback.
  const [resolvedRedirectUri, setResolvedRedirectUri] = useState<string | null>(
    null
  )

  const submit = useCallback(
    async (consentGranted: boolean) => {
      if (!oauthParams) {
        return
      }

      setStatus('submitting')

      try {
        const { redirect_uri } = await stytch.idp.oauthAuthorizeSubmit({
          consent_granted: consentGranted,
          client_id: oauthParams.client_id,
          redirect_uri: oauthParams.redirect_uri,
          response_type: oauthParams.response_type,
          scopes: oauthParams.scopes,
          state: oauthParams.state,
          nonce: oauthParams.nonce,
          code_challenge: oauthParams.code_challenge,
          prompt: oauthParams.prompt
        })

        if (!consentGranted) {
          window.location.href = redirect_uri
          return
        }

        // On approval, briefly show success, then redirect back to the client with the auth code.
        setResolvedRedirectUri(redirect_uri)
        setStatus('success')
        window.setTimeout(() => {
          window.location.href = redirect_uri
        }, SUCCESS_REDIRECT_DELAY_MS)
      } catch {
        setStatus('error')
      }
    },
    [oauthParams, stytch.idp]
  )

  useEffect(() => {
    if (!oauthParams) {
      setStatus('error')
      return
    }

    let cancelled = false

    stytch.idp
      .oauthAuthorizeStart({
        client_id: oauthParams.client_id,
        redirect_uri: oauthParams.redirect_uri,
        response_type: oauthParams.response_type,
        scopes: oauthParams.scopes,
        prompt: oauthParams.prompt
      })
      .then(response => {
        if (cancelled) {
          return
        }

        // Skip the screen only when Stytch *explicitly* says consent isn't required;
        // `=== false` (not `!value`) shows the card on any unexpected/missing field rather than auto-approving.
        if (response.consent_required === false) {
          void submit(true)
          return
        }

        setStatus('consent')
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [oauthParams, stytch.idp, submit])

  const approve = useCallback(() => submit(true), [submit])
  const deny = useCallback(() => submit(false), [submit])

  return {
    status,
    redirectUri: oauthParams?.redirect_uri ?? null,
    resolvedRedirectUri,
    approve,
    deny
  }
}
